import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getLanguageColor } from '../common/languageUtils';

const { FiArrowLeft, FiCopy, FiCheck, FiTag, FiBox, FiEdit2, FiTrash2, FiInfo } = FiIcons;

export default function SnippetDetail({ snippet, onBack, onEdit, onDelete }) {
  const [copied, setCopied] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(snippet.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!snippet) return null;

  const tags = typeof snippet.tags === 'string' 
    ? snippet.tags.split(',').filter(Boolean) 
    : (snippet.tags || []);

  const languageStyles = getLanguageColor(snippet.language);

  return (
    <div className="flex-1 overflow-y-auto bg-slate-950 p-6 md:p-8 custom-scrollbar">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium"
          >
            <SafeIcon icon={FiArrowLeft} />
            Back to Vault
          </button>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => onEdit(snippet)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition-all text-sm font-medium border border-slate-700"
            >
              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
              Edit
            </button>
            <button 
              onClick={() => setShowConfirmDelete(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all text-sm font-medium border border-red-500/20"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {showConfirmDelete && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-between animate-in fade-in slide-in-from-top-4 duration-300">
            <p className="text-red-400 text-sm font-medium">Are you sure you want to permanently delete this snippet?</p>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowConfirmDelete(false)}
                className="text-slate-400 hover:text-white text-sm font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={() => onDelete(snippet.id)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        )}

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-2.5 py-1 rounded text-xs font-mono uppercase font-bold border ${languageStyles}`}>
              {snippet.language}
            </span>
            <h1 className="text-3xl font-bold text-slate-100 tracking-tight">
              {snippet.title}
            </h1>
          </div>
          
          <p className="text-slate-400 text-lg mt-4 leading-relaxed whitespace-pre-wrap">
            {snippet.description}
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-slate-800">
            <div className="flex items-center gap-2 text-sm text-slate-300 bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-800">
              <SafeIcon icon={FiBox} className="text-slate-500" />
              <span className="font-medium text-slate-400">Framework:</span> {snippet.framework}
            </div>
            <div className="flex items-center gap-2 pl-2">
              <SafeIcon icon={FiTag} className="text-slate-500 w-4 h-4" />
              <div className="flex gap-2 flex-wrap">
                {tags.map(tag => (
                  <span key={tag} className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded-md border border-slate-700/50">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {snippet.additional_instructions && (
          <div className="mb-8 p-6 bg-indigo-500/5 border border-indigo-500/10 rounded-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <SafeIcon icon={FiInfo} className="w-12 h-12 text-indigo-400" />
            </div>
            <h3 className="text-indigo-400 text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
              <SafeIcon icon={FiInfo} className="w-3 h-3" />
              Additional Instructions
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap relative z-10">
              {snippet.additional_instructions}
            </p>
          </div>
        )}

        <div className="relative rounded-xl overflow-hidden border border-slate-800 bg-[#1E1E1E] shadow-2xl mb-12">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-800">
            <div className="flex gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-700"></div>
              <div className="w-3 h-3 rounded-full bg-slate-700"></div>
              <div className="w-3 h-3 rounded-full bg-slate-700"></div>
            </div>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                copied 
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
              }`}
            >
              {copied ? (
                <>
                  <SafeIcon icon={FiCheck} /> Copied!
                </>
              ) : (
                <>
                  <SafeIcon icon={FiCopy} /> Copy Code
                </>
              )}
            </button>
          </div>
          <div className="text-sm">
            <SyntaxHighlighter 
              language={snippet.language.toLowerCase()} 
              style={vscDarkPlus}
              customStyle={{
                margin: 0,
                padding: '1.5rem',
                background: 'transparent',
              }}
              showLineNumbers={true}
            >
              {snippet.code || ''}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>
    </div>
  );
}